import { AxiosError } from 'axios';
import { authHeader, authUploadService } from 'services';
import { IPartnerPreRegistration } from 'types/Auth';
import { IPartner, IPartnerCreate } from 'types/Partner';
import { getUserToken } from 'util/auth';

import { Api } from './api/axios.service';

const FILE_DOWNLOAD_URL = `${process.env.REACT_APP_BASE_API_URL}/api/partner`;

export /**
 * Return partnership terms
 *
 * @return {*}
 */
const getPartnershipTerms = async () => {
  try {
    const { data } = await Api.get<string>('partner-agreement', {
      params: {
        access_token: `Bearer ${getUserToken().token}`,
      },
    });

    return data;
  } catch (reason) {
    throw new Error('Não foi possível obter o contrato de parceria');
  }
};

const getPartnersList = (
  page: number,
  amount: number,
  filter: string,
  uf: string
) => {
  return Api.get(
    `partner?page=${page}&amount=${amount}&filter=${filter}&uf=${uf}&status=all`,
    {
      headers: authHeader(),
    }
  );
};

const updatePartner = (id: string, partner: any) => {
  let formData = new FormData();
  const keys = Object.keys(partner);

  keys.forEach((key) => {
    switch (key) {
      case 'partnershipContract':
        if (partner[key] instanceof File) {
          formData.append(key, partner[key]);
        }
        break;
      default:
        formData.append(key, partner[key]);
        break;
    }
  });

  return Api.put(`/partner/${id}`, formData, {
    headers: authUploadService(),
  }).then((response) => response);
};

const signUpPartner = (partner: any) => {
  let formData = new FormData();
  const keys = Object.keys(partner);

  keys.forEach((key) => {
    formData.append(key, partner[key]);
  });

  return Api.post('/partner', formData, {
    headers: authUploadService(),
  }).then((response) => response.data);
};

const getAllPartners = () => {
  // FIXME: Add pagination in partner autocomplete
  return Api.get('/partner?status=approved&amount=100000&filter=&uf=', {
    headers: authHeader(),
  });
};

const getPartnerMaxPage = (amount: number, filter: string, uf: string) => {
  return Api.get(`partner?amount=${amount}&filter=${filter}&uf=${uf}`, {
    headers: authHeader(),
  });
};

const getCurrentUser = () => {
  return Api.get('/user', {
    headers: authHeader(),
  });
};

/**
 * Register or update a partner from admin
 *
 * @param {IPartnerCreate} partner
 * @return {*}  {Promise<void>}
 */
const createOrUpdatePartner = async (
  partner: IPartnerCreate
): Promise<void> => {
  const formData = new FormData();

  if (!partner.cnpj) {
    partner.cnpj = null;
  }

  if (!partner.cpf) {
    partner.cpf = null;
  }

  Object.entries(partner).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }

    if (!(value instanceof File) && typeof value === 'object') {
      return formData.append(key, JSON.stringify(value));
    }

    formData.append(key, value);
  });

  try {
    /* If has id should update */
    if (partner._id) {
      await Api.put(`/partner/${partner._id}`, formData, {
        headers: authUploadService(),
      });

      return;
    }

    await Api.post('/partner', formData, {
      headers: authUploadService(),
    });
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error ||
        (partner._id
          ? 'Não foi possível atualizar o parceiro'
          : 'Não foi possível cadastrar o parceiro')
    );
  }
};

/**
 * Find partner by id
 *
 * @param {{ id: string }} { id }
 * @return {*}  {Promise<IPartner>}
 */
const findById = async ({ id }: { id: string }): Promise<IPartner> => {
  try {
    const { data } = await Api.get<IPartner>(`/partner/by-id/${id}`, {
      headers: authUploadService(),
    });
    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível buscar o parceiro'
    );
  }
};

/**
 * Get partner image by partnerId
 *
 * @param {string} partnerId
 * @return {*}  {string}
 */
const getPartnerImage = (partnerId?: string): string => {
  if (!partnerId) {
    return '';
  }

  return `${FILE_DOWNLOAD_URL}/image-download/${partnerId}/?access_token=Bearer ${
    getUserToken().token
  }`;
};

/**
 * Get partner contract by partnerId
 *
 * @param {string} partnerId
 * @return {*}  {string}
 */
const getPartnerContract = (partnerId?: string): string => {
  if (!partnerId) {
    return '';
  }

  return `${FILE_DOWNLOAD_URL}/contract-download/${partnerId}/?access_token=Bearer ${
    getUserToken().token
  }`;
};

/**
 * Sign up a new partner
 *
 * @param {IPartnerPreRegistration} params
 * @return {*}
 */
const partnerSignUp = async (params: IPartnerPreRegistration) => {
  try {
    const { data } = await Api.post<IPartner>(
      '/partner/create-by-partner',
      params
    );

    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível cadastrar o parceiro'
    );
  }
};

/**
 * Update partner user profile from signed in partner user
 *
 * @param {IPartnerCreate} partner
 * @return {*}  {Promise<void>}
 */
const partnerUpdateProfile = async (partner: IPartnerCreate): Promise<void> => {
  const formData = new FormData();

  if (!partner.cnpj) {
    partner.cnpj = null;
  }

  if (!partner.cpf) {
    partner.cpf = null;
  }

  Object.entries(partner).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }

    if (!(value instanceof File) && typeof value === 'object') {
      return formData.append(key, JSON.stringify(value));
    }

    formData.append(key, value);
  });

  try {
    await Api.put(`/partner/update-by-partner`, formData, {
      headers: authUploadService(),
    });
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível atualizar o parceiro'
    );
  }
};

/* Delete partner by id
 *
 * @param {(string | undefined)} partnerId
 * @return {*}
 */
const deletePartner = (partnerId: string): Promise<any> =>
  Api.delete(`/partner/${partnerId}`, {
    headers: authHeader(),
  });

/**
 *  Send message for partner accepted
 *
 * @param {string} partnerId
 * @return {*}  {Promise<any>}
 */
const acceptPartner = (partnerId: string): Promise<any> =>
  Api.put(
    `/partner-audit/approve-partner/${partnerId}`,
    {},
    {
      headers: authHeader(),
    }
  );

/**
 *  Send message for partner rejected
 *
 * @param {string} partnerId
 * @return {*}  {Promise<any>}
 */
const rejectPartner = (
  partnerId: string,
  data: { message: string }
): Promise<any> =>
  Api.put(`/partner-audit/reject-partner/${partnerId}`, data, {
    headers: authHeader(),
  });

/**
 * Get list of discount from partner
 *
 * @param {string} sort
 * @param {string} partnerId
 * @return {*}
 */
const getPartnerDiscount = (sort: string, partnerId: string): any => {
  return Api.get(
    `partner-audit/findAllById/?sortDate=${sort}&id=${partnerId}`,
    {
      headers: authHeader(),
    }
  );
};

export const PartnerService = {
  getPartnersList,
  getPartnerMaxPage,
  getCurrentUser,
  getAllPartners,
  signUpPartner,
  updatePartner,
  createOrUpdatePartner,
  findById,
  getPartnerImage,
  getPartnerContract,
  partnerSignUp,
  partnerUpdateProfile,
  deletePartner,
  acceptPartner,
  rejectPartner,
  getPartnershipTerms,
  getPartnerDiscount,
};
